import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const RECENT_PRODUCTS_KEY = 'product_search_recent_v2';
const MAX_RECENT = 10;

function loadRecentProducts() {
  try {
    const stored = localStorage.getItem(RECENT_PRODUCTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}
function saveRecentProducts(products) {
  try {
    localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(products.slice(0, MAX_RECENT)));
  } catch { /* ignore */ }
}

/**
 * Hook for product autocomplete. Debounces queries, aborts in-flight on
 * restart, caches recent selections in localStorage.
 *
 * @param {object} options
 *   debounceMs   - default 300
 *   minChars     - default 2
 *   vendorFilter - optional vendor_name; ILIKE-restricts results server-side
 *
 * Returns: { query, setQuery, results, loading, error, recentProducts,
 *            addToRecent, clearRecent, clearSearch, search }
 *
 * 🔴 `search(q)` ist der erneute Versuch fuer den Fehlerzustand des
 * Aufrufers (G15e NB3): der Effekt debounced nur bei geaenderter `query`, ein
 * zweiter Aufruf mit DEMSELBEN Begriff liefe sonst nicht. Additiv — wer sie
 * nicht braucht, ignoriert sie.
 *
 * Result shape (from /api/search/products):
 *   { sku, product_name, variant_name, product_handle, vendor_name, vendor_id,
 *     cost_per_item, currency, product_status, current_inventory,
 *     product_id,      // die SHOPIFY-Produkt-Id (eine je Produkt)
 *     product_row_id } // shp_products.id, die Zeile je Variante (seit EK-C3)
 */
export default function useProductSearch(options = {}) {
  const { debounceMs = 300, minChars = 2, vendorFilter = null } = options;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentProducts, setRecentProducts] = useState(() => loadRecentProducts());

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const search = useCallback(async (searchQuery) => {
    if (abortRef.current) abortRef.current.abort();
    const trimmed = (searchQuery || '').trim();
    if (trimmed.length < minChars) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      abortRef.current = new AbortController();
      const params = { q: trimmed };
      if (vendorFilter) params.vendor = vendorFilter;
      const { data } = await axios.get(`${API_BASE}/api/search/products`, {
        params,
        signal: abortRef.current.signal,
      });
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error('Product search failed:', err);
        setError(err.message || 'Search failed');
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  }, [minChars, vendorFilter]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), debounceMs);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, debounceMs, search]);

  const addToRecent = useCallback((product) => {
    if (!product?.sku) return;
    setRecentProducts(prev => {
      const filtered = prev.filter(p => p.sku !== product.sku);
      const updated = [product, ...filtered].slice(0, MAX_RECENT);
      saveRecentProducts(updated);
      return updated;
    });
  }, []);

  const clearRecent = useCallback(() => {
    setRecentProducts([]);
    localStorage.removeItem(RECENT_PRODUCTS_KEY);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  return {
    query, setQuery,
    results, loading, error,
    recentProducts, addToRecent, clearRecent,
    clearSearch,
    search,
  };
}
